/**
 * Pure step arithmetic for the shared WizardDialog, kept out of the component so it can be
 * unit-tested without a renderer.
 */

/** The minimum a step needs for the counter and navigation. */
export interface CountableStep {
	key: string;
	/** Defaults to true. False for a preamble (e.g. Select Platform) that the "Step n of N" counter skips. */
	counted?: boolean;
}

const isCounted = (step: CountableStep) => step.counted !== false;

/** Clamp an index into the step range; an empty wizard clamps to 0. */
export const clampStep = (index: number, stepCount: number): number => {
	if (stepCount <= 0 || !Number.isFinite(index)) {
		return 0;
	}
	return Math.min(Math.max(Math.trunc(index), 0), stepCount - 1);
};

/** Resolve a step reference (index or key) to an index, or undefined when the key is unknown. */
export const resolveStep = (steps: CountableStep[], ref: number | string): number | undefined => {
	if (typeof ref === "number") {
		return clampStep(ref, steps.length);
	}
	const index = steps.findIndex((step) => step.key === ref);
	return index === -1 ? undefined : index;
};

/**
 * The "Step n of N" label for the step at `index`, counting only counted steps.
 * Undefined for an uncounted step, which shows no counter.
 */
export const stepCounter = (steps: CountableStep[], index: number): { current: number; total: number } | undefined => {
	const step = steps[index];
	if (!step || !isCounted(step)) {
		return undefined;
	}
	const current = steps.slice(0, index + 1).filter(isCounted).length;
	const total = steps.filter(isCounted).length;
	return { current, total };
};

export const isFirstStep = (index: number) => index <= 0;

export const isLastStep = (index: number, stepCount: number) => index >= stepCount - 1;
